import React, { useEffect, useState } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { UserPlus, Clock } from 'lucide-react';
import { useFollow } from '@/contexts/FollowContext';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { getInitials, getNotificationIcon } from './NotificationUtils';
import { useNotifications, type NotificationItem } from '@/hooks/useNotifications';
import { cn } from '@/lib/utils';
import { getProfileUrl } from '@/lib/profileUrl';

interface FollowerNotificationItemProps {
  notification: NotificationItem;
  isUnread: boolean;
  onClose: () => void;
}

export function FollowerNotificationItem({ notification, isUnread, onClose }: FollowerNotificationItemProps) {
  const { followUser, isFollowing } = useFollow();
  const { checkFollowingBack } = useNotifications();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isFollowingBack, setIsFollowingBack] = useState(false);

  const { data } = notification;
  const followerId = data.follower_id;
  const followerUsername = data.follower_username;

  useEffect(() => {
    if (followerId) {
      checkFollowingBack(followerId).then(setIsFollowingBack);
    }
  }, [followerId, checkFollowingBack]);

  const handleFollowBack = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!followerId) return;
    
    const result = await followUser(followerId);
    if (result.success) {
      setIsFollowingBack(true);
      toast({
        title: 'Following back!',
        description: 'You are now following this user.',
      });
    } else {
      toast({
        title: 'Failed to follow back',
        description: result.error,
        variant: 'destructive'
      });
    }
  };

  const handleClick = () => {
    if (followerId) {
      navigate(getProfileUrl(followerId, followerUsername));
      onClose();
    }
  };

  const showFollowingBadge = isFollowingBack || (followerId && isFollowing(followerId));

  return (
    <div
      className={cn(
        "flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer",
        isUnread && "bg-primary/5 border-l-2 border-primary"
      )}
      onClick={handleClick}
    >
      <Avatar className="h-12 w-12 flex-shrink-0">
        <AvatarImage src={data.follower_avatar_url || ''} />
        <AvatarFallback className="bg-primary/10 text-primary">
          {getInitials(data.follower_name, data.follower_username)}
        </AvatarFallback>
      </Avatar>
      
      <div className="flex-1 min-w-0 space-y-2">
        <div>
          <div className="flex items-center gap-1 mb-1">
            {getNotificationIcon('follower')}
            <p className={cn(
              "text-foreground text-sm leading-tight",
              isUnread ? "font-semibold" : "font-medium"
            )}>
              {data.follower_name || data.follower_username || 'Someone'} started following you
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-3 w-3" />
            <span>
              {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
            </span>
          </div>
        </div>
        
        <div className="flex justify-start">
          {showFollowingBadge ? (
            <Badge variant="secondary" className="text-xs">
              Following
            </Badge>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={handleFollowBack}
              className="text-xs px-3"
            >
              <UserPlus className="h-3 w-3 mr-1" />
              Follow Back
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
